import * as THREE from 'three';
import { BaseEnemy } from './base.js';
import { ENEMY_STATS } from '../../data/enemies.data.js';
import { circleVsCircle } from '../../engine/collision.js';
import { animateEnemyDeath } from '../ui/animator.js';

export class Minmon extends BaseEnemy {
  constructor(opts) {
    super({ ...opts, stats: ENEMY_STATS.minmon });
    this.strafeDir = Math.random() < 0.5 ? 1 : -1;
    this.strafeTimer = 0.3 + Math.random() * 0.6;
    this.hp = Math.max(1, this.maxHp * 0.62);
    this.maxHp = this.hp;

    this._bomber = {
      armed: false,
      armDuration: 0.72,
      armTimer: 0,
      triggerRadius: Math.max(0.95, this.stats.attackRange * 1.35),
      blastRadius: Math.max(1.9, this.stats.attackRange * 2.35),
      blastDamage: Math.max(7, this.stats.meleeDamage * 1.35),
    };
    this._exploded = false;

    this._particlePool = [];
    this._activeParticles = [];
    this._particleClock = 0;
    this._burstCooldown = 0;

    this._waddlePhase = Math.random() * Math.PI * 2;
    this._waddleNodes = null;
    this._waddleEnergy = 0;
    this._prevAnimX = this.x;
    this._prevAnimZ = this.z;
  }

  buildMesh() {
    super.buildMesh();
    this.group.scale.setScalar(0.78);
    this._cacheWaddleNodes();

    const core = new THREE.Mesh(
      new THREE.SphereGeometry(0.2, 8, 8),
      new THREE.MeshStandardMaterial({
        color: this.stats.accentColor,
        emissive: this.stats.accentColor,
        emissiveIntensity: 0.95,
        roughness: 0.25,
        metalness: 0.1,
      })
    );
    core.position.set(0, 1.15, 0.36);
    this.group.add(core);
    this._core = core;

    const blastRing = new THREE.Mesh(
      new THREE.RingGeometry(0.35, 0.7, 24),
      new THREE.MeshBasicMaterial({
        color: this.stats.accentColor,
        transparent: true,
        opacity: 0,
        side: THREE.DoubleSide,
        depthWrite: false,
      })
    );
    blastRing.rotation.x = -Math.PI / 2;
    blastRing.position.set(0, 0.05, 0);
    blastRing.visible = false;
    this.group.add(blastRing);
    this._blastRing = blastRing;
  }

  _cacheWaddleNodes() {
    // BaseEnemy builds body/head/visor/legs in order; cache safe references once.
    const body = this.group.children[0] ?? null;
    const head = this.group.children[1] ?? null;
    const legL = this.group.children[3] ?? null;
    const legR = this.group.children[4] ?? null;
    this._waddleNodes = {
      body,
      head,
      legL,
      legR,
      bodyBaseX: body?.position?.x ?? 0,
      headBaseX: head?.position?.x ?? 0,
      bodyBaseY: body?.position?.y ?? 0.9,
      headBaseY: head?.position?.y ?? 1.62,
    };
  }

  _updateWaddlePose(dt) {
    const nodes = this._waddleNodes;
    if (!nodes) return;

    const stepDist = Math.hypot(this.x - this._prevAnimX, this.z - this._prevAnimZ);
    this._prevAnimX = this.x;
    this._prevAnimZ = this.z;
    const speed = dt > 0 ? stepDist / dt : 0;
    const speedNorm = THREE.MathUtils.clamp(speed / Math.max(0.001, this.stats.moveSpeed), 0, 1.8);
    const moving = speedNorm > 0.12;

    // Drive animation by distance traveled so gait doesn't feel like a static loop.
    if (moving) {
      this._waddlePhase += stepDist * (22 + speedNorm * 11);
      this._waddleEnergy = THREE.MathUtils.lerp(this._waddleEnergy, 1, 0.22);
    } else {
      this._waddlePhase += dt * 1.9;
      this._waddleEnergy = THREE.MathUtils.lerp(this._waddleEnergy, 0.18, 0.08);
    }

    const s = Math.sin(this._waddlePhase);
    const c = Math.cos(this._waddlePhase);
    const s2 = Math.sin(this._waddlePhase * 2);
    const amp = (0.55 + speedNorm * 1.45) * this._waddleEnergy;
    const lateral = speed > 0.001 ? ((-this.aimZ * this.vx) + (this.aimX * this.vz)) / speed : 0;
    const strafeLean = THREE.MathUtils.clamp(lateral, -1, 1);

    if (nodes.legL) {
      nodes.legL.rotation.z = 0.32 * s * amp;
      nodes.legL.rotation.x = 0.17 * c * amp;
      nodes.legL.position.x = -0.2 + 0.018 * s * amp;
    }
    if (nodes.legR) {
      nodes.legR.rotation.z = -0.32 * s * amp;
      nodes.legR.rotation.x = -0.17 * c * amp;
      nodes.legR.position.x = 0.2 + 0.018 * s * amp;
    }
    if (nodes.body) {
      // Chicken-like gait: dominant lateral sway + quick side snap.
      nodes.body.position.x = nodes.bodyBaseX + (0.125 * s + 0.04 * s2) * amp;
      nodes.body.position.y = nodes.bodyBaseY + 0.015 * Math.abs(c) * amp;
      nodes.body.rotation.z = (0.2 * s + 0.06 * s2) * amp + strafeLean * 0.24;
      nodes.body.rotation.x = 0.035 * c * amp;
    }
    if (nodes.head) {
      // Head follows sway but a bit damped so it feels cute/readable.
      nodes.head.position.x = nodes.headBaseX + (0.085 * s + 0.022 * s2) * amp;
      nodes.head.position.y = nodes.headBaseY + 0.012 * Math.abs(c) * amp;
      nodes.head.rotation.z = (0.12 * s + 0.03 * s2) * amp + strafeLean * 0.12;
      nodes.head.rotation.x = -0.02 * c * amp;
    }
  }

  updateAI(dt, game) {
    const player = game.player;
    if (!player.alive) return;

    this.sensePlayer(game, dt);
    const d = this.distTo(player.x, player.z);

    const targetX = player.x;
    const targetZ = player.z;

    this.aimX = targetX - this.x;
    this.aimZ = targetZ - this.z;
    const aimLen = Math.hypot(this.aimX, this.aimZ) || 1;
    this.aimX /= aimLen;
    this.aimZ /= aimLen;

    this._updateEmitterParticles(dt, game);

    if (this._bomber.armed) {
      this._bomber.armTimer -= dt;
      this.state = 'arming';
      this.vx = 0;
      this.vz = 0;
      const armProgress = 1 - Math.max(0, this._bomber.armTimer) / this._bomber.armDuration;
      this._updateCoreBlink(armProgress);
      if (this._bomber.armTimer <= 0) {
        this._detonate(game, { awardKill: true });
      }
      return;
    }

    this.strafeTimer -= dt;
    if (this.strafeTimer <= 0) {
      this.strafeDir = Math.random() < 0.5 ? 1 : -1;
      this.strafeTimer = 0.2 + Math.random() * 0.7;
    }

    const nearBand = this.stats.attackRange * 2.1;
    if (d > nearBand || !this._lastCanSeePlayer) {
      this.state = 'chase';
      this.moveWithPathfinding(targetX, targetZ, dt, game, 1.2);
    } else {
      // Keep moving during pressure so packs naturally spread around the player.
      const sideX = -this.aimZ * this.strafeDir;
      const sideZ = this.aimX * this.strafeDir;
      const tx = player.x + sideX * 1.7;
      const tz = player.z + sideZ * 1.7;
      this.state = 'swarm';
      this.moveToward(tx, tz, dt, game.room.walls, 1.08);

      // Movement rewrites aim; restore aim toward target for cleaner facing.
      this.aimX = targetX - this.x;
      this.aimZ = targetZ - this.z;
      const restoreLen = Math.hypot(this.aimX, this.aimZ) || 1;
      this.aimX /= restoreLen;
      this.aimZ /= restoreLen;
    }

    if (this._core?.material) {
      this._core.material.emissiveIntensity = this.state === 'swarm' ? 1.35 : 0.92;
    }

    if (circleVsCircle(this.x, this.z, this._bomber.triggerRadius, player.x, player.z, player.radius)) {
      this._startArming();
    }
  }

  _startArming() {
    if (this._bomber.armed || !this.alive || this._exploded) return;
    this._bomber.armed = true;
    this._bomber.armTimer = this._bomber.armDuration;
    this.state = 'arming';
  }

  _updateCoreBlink(progress) {
    if (!this._core?.material) return;
    const freq = 10 + progress * 26;
    const blink = 0.55 + 0.45 * Math.abs(Math.sin(this.stateTime * freq));
    this._core.material.emissiveIntensity = 1.1 + blink * (1.1 + progress * 1.9);
  }

  _spawnParticle(strength = 1) {
    if (!this.group) return;
    let p = this._particlePool.pop();
    if (!p) {
      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(0.06, 5, 5),
        new THREE.MeshBasicMaterial({
          color: this.stats.accentColor,
          transparent: true,
          opacity: 0.9,
          depthWrite: false,
        })
      );
      p = { mesh, life: 0, maxLife: 0.5, vx: 0, vy: 0, vz: 0 };
      this.group.add(mesh);
    } else {
      p.mesh.visible = true;
    }
    const ang = Math.random() * Math.PI * 2;
    const spread = 0.12 + Math.random() * 0.22;
    p.mesh.position.set(
      Math.cos(ang) * spread,
      0.95 + Math.random() * 0.45,
      Math.sin(ang) * spread + 0.1
    );
    p.mesh.scale.setScalar(0.75 + Math.random() * 0.65 * strength);
    p.maxLife = 0.22 + Math.random() * 0.26;
    p.life = p.maxLife;
    p.vx = (Math.random() - 0.5) * (0.55 + strength * 0.65);
    p.vy = 0.3 + Math.random() * (0.55 + strength * 0.9);
    p.vz = (Math.random() - 0.5) * (0.55 + strength * 0.65);
    this._activeParticles.push(p);
  }

  _updateEmitterParticles(dt, game) {
    const armProgress = this._bomber.armed
      ? 1 - Math.max(0, this._bomber.armTimer) / this._bomber.armDuration
      : 0;
    const spawnRate = this._bomber.armed ? (16 + armProgress * 48) : 7;
    this._particleClock += dt * spawnRate;
    while (this._particleClock >= 1) {
      this._particleClock -= 1;
      this._spawnParticle(this._bomber.armed ? 0.9 + armProgress * 1.3 : 0.55);
    }

    for (let i = this._activeParticles.length - 1; i >= 0; i -= 1) {
      const p = this._activeParticles[i];
      p.life -= dt;
      if (p.life <= 0) {
        p.mesh.visible = false;
        this._activeParticles.splice(i, 1);
        this._particlePool.push(p);
        continue;
      }
      p.mesh.position.x += p.vx * dt;
      p.mesh.position.y += p.vy * dt;
      p.mesh.position.z += p.vz * dt;
      p.vy -= (this._bomber.armed ? 1.9 : 1.3) * dt;
      const t = p.life / p.maxLife;
      p.mesh.material.opacity = t * (this._bomber.armed ? 1 : 0.75);
      p.mesh.scale.multiplyScalar(0.987 + (this._bomber.armed ? 0.012 : 0));
      if (game?.illuminationAt) {
        const illum = game.illuminationAt(this.x, this.z);
        p.mesh.material.color.set(this.stats.accentColor).multiplyScalar(0.85 + 0.35 * illum);
      }
    }
  }

  _triggerExplosionFx(game) {
    game?._flashCircle?.(this.x, this.z, this._bomber.blastRadius);
    game?.audio?.play?.('launcherExplode', { pitchJitter: 0.05 });
    game?.cameraRig?.shake?.(0.25, 0.18, this.aimX, this.aimZ);

    if (this._blastRing?.material) {
      this._blastRing.visible = true;
      this._blastRing.scale.setScalar(0.35);
      this._blastRing.material.opacity = 0.95;
      this._burstCooldown = 0.22;
    }

    const burstCount = 22;
    for (let i = 0; i < burstCount; i += 1) this._spawnParticle(2.2);
  }

  _applyExplosionDamage(game) {
    const player = game?.player;
    if (player?.alive && circleVsCircle(this.x, this.z, this._bomber.blastRadius, player.x, player.z, player.radius)) {
      player.takeDamage(this._bomber.blastDamage, game);
    }

    if (Array.isArray(game?.enemies)) {
      for (const enemy of game.enemies) {
        if (!enemy || enemy === this || !enemy.alive) continue;
        if (circleVsCircle(this.x, this.z, this._bomber.blastRadius, enemy.x, enemy.z, enemy.radius || 0.4)) {
          enemy.takeDamage(this._bomber.blastDamage, game);
        }
      }
    }
  }

  _detonate(game, { awardKill = false, playDeathAnim = true } = {}) {
    if (this._exploded) return;
    this._exploded = true;
    this._triggerExplosionFx(game);
    this._applyExplosionDamage(game);
    this.alive = false;
    this.state = 'dead';
    if (playDeathAnim) {
      animateEnemyDeath(this.group, () => {
        this._readyToDispose = true;
      });
    }
    if (awardKill) game?.onEnemyKilled?.(this);
  }

  takeDamage(n, game) {
    if (!this.alive) return false;
    const willDie = this.hp - Math.max(0.2, n * (this.stats.damageTakenMul ?? 1)) <= 0;
    const died = super.takeDamage(n, game);
    if (died && willDie) {
      this._detonate(game, { awardKill: false, playDeathAnim: false });
    }
    return died;
  }

  update(dt, game, options = {}) {
    super.update(dt, game, options);
    if (!options?.skipPresentation) this._updateWaddlePose(dt);
    if (this._burstCooldown > 0 && this._blastRing?.material) {
      this._burstCooldown -= dt;
      const t = Math.max(0, this._burstCooldown / 0.22);
      this._blastRing.scale.setScalar(1.2 + (1 - t) * 3.7);
      this._blastRing.material.opacity = t * 0.9;
      if (t <= 0) {
        this._blastRing.visible = false;
      }
    }
  }

  dispose() {
    this._activeParticles.length = 0;
    this._particlePool.length = 0;
    super.dispose();
  }
}
